import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import type { SeasonalMonth } from '../../utils/seasonalMonth';
import styles from './SeasonalMonthSelector.module.scss';

const getMonthLabel = (month: SeasonalMonth, intl: ReturnType<typeof useIntl>): string => {
  // Create a date for the given month (using year 2000 as arbitrary year)
  const date = new Date(2000, month - 1, 1);
  return intl.formatDate(date, { month: 'long' });
};

type SeasonalMonthSelectorProps = {
  selectedMonth: SeasonalMonth;
  onMonthChange: (month: SeasonalMonth) => void;
  className?: string;
};

const allMonths: SeasonalMonth[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const SeasonalMonthSelector = ({ selectedMonth, onMonthChange, className }: SeasonalMonthSelectorProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const selectedMonthLabel = useMemo(() => getMonthLabel(selectedMonth, intl), [selectedMonth, intl]);

  const monthOptions = useMemo(() => {
    return allMonths.map(month => ({
      key: month.toString(),
      title: getMonthLabel(month, intl),
      selected: month === selectedMonth,
      onClick: () => {
        onMonthChange(month);
        setIsOpen(false);
      }
    }));
  }, [selectedMonth, intl, onMonthChange]);

  return (
    <div className={classNames(styles.container, className)}>
      <span className={styles.labelText}>
        <FormattedMessage defaultMessage="Most popular trails in" />
      </span>{' '}
      <DropdownMenu
        anchor={
          <button
            className={styles.dropdownAnchor}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={intl.formatMessage({ defaultMessage: 'Select month' })}
          >
            <span className={styles.dropdownAnchorContents}>
              <span>{selectedMonthLabel}</span>
              <ChevronSm orientation={isOpen ? 'up' : 'down'} size="sm" />
            </span>
          </button>
        }
        className={styles.dropdown}
        isOpen={isOpen}
        onCloseRequest={() => setIsOpen(false)}
        options={monthOptions}
        placement="bottom-start"
      />
    </div>
  );
};

export default SeasonalMonthSelector;
